/**
 * Normalise un texte pour la recherche (enlève les accents, met en minuscule)
 */
function normalizeText(text) {
  if (!text) return '';
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, ''); // Enlève les accents
}

/**
 * Calcule le prix unitaire normalisé d'un produit
 */
function calculateUnitPrice(product) {
  if (!product.variants || product.variants.length === 0) return Infinity;

  let bestUnitPrice = Infinity;

  for (const variant of product.variants) {
    const price = variant.reducedPrice != null ? variant.reducedPrice : variant.referencePrice;
    if (price === null || price === 0) continue;

    // Pour simplifier, on considère que le prix est déjà unitaire
    // Si besoin, on peut parser la quantité comme dans page.tsx
    const unitPrice = price;
    if (unitPrice < bestUnitPrice) {
      bestUnitPrice = unitPrice;
    }
  }

  return bestUnitPrice;
}

/**
 * Recherche et filtre les produits selon la query, la marque et les sous-catégories
 */
export function searchProducts(
  allProducts,
  query,
  selectedBrand,
  selectedSubCategories
) {
  if (!query.trim()) {
    return [];
  }

  const normalizedQuery = normalizeText(query);

  // Filtrer et scorer les produits
  const scoredProducts = allProducts
    .map(product => {
      const nameNormalized = normalizeText(product.name);
      const categoryNormalized = normalizeText(product.category);
      const subCategoryNormalized = normalizeText(product.subCategory);
      const subSubCategoryNormalized = normalizeText(product.subSubCategory || '');
      const brandNormalized = normalizeText(product.brand);
      const descriptionNormalized = normalizeText(product.description || '');

      let score = 0;
      let matches = false;

      // Bonus si le nom commence par le mot recherché
      if (nameNormalized.startsWith(normalizedQuery)) {
        score += 200;
        matches = true;
      }
      // Sinon, bonus si le mot recherché est dans le nom
      else if (nameNormalized.includes(normalizedQuery)) {
        score += 100;
        matches = true;
      }

      // Bonus important si la sous-catégorie correspond exactement
      if (subCategoryNormalized === normalizedQuery) {
        score += 80;
        matches = true;
      }
      // Ou si elle contient le mot recherché
      else if (subCategoryNormalized.includes(normalizedQuery)) {
        score += 50;
        matches = true;
      }

      // Bonus si la sous-sous-catégorie correspond
      if (subSubCategoryNormalized === normalizedQuery) {
        score += 70;
        matches = true;
      } else if (subSubCategoryNormalized.includes(normalizedQuery)) {
        score += 40;
        matches = true;
      }

      if (categoryNormalized.includes(normalizedQuery)) {
        score += 30;
        matches = true;
      }
      if (brandNormalized.includes(normalizedQuery)) {
        score += 20;
        matches = true;
      }
      if (descriptionNormalized.includes(normalizedQuery)) {
        score += 10;
        matches = true;
      }

      if (!matches) return null;

      // Calculer le prix unitaire pour ce produit
      const unitPrice = calculateUnitPrice(product);

      return { product, score, unitPrice };
    })
    .filter(item => item !== null);

  // Appliquer le filtre par marque
  let filteredProducts = scoredProducts;
  if (selectedBrand !== 'Toutes les marques') {
    filteredProducts = scoredProducts.filter(item => item.product.brand === selectedBrand);
  }

  // Appliquer le filtre par sous-catégorie
  if (selectedSubCategories.length > 0) {
    filteredProducts = filteredProducts.filter(item =>
      selectedSubCategories.includes(item.product.subCategory)
    );
  }

  // Trier avec un algorithme hybride : score de pertinence ET prix unitaire
  return filteredProducts
    .sort((a, b) => {
      // Si les scores sont très différents (écart > 100), privilégier la pertinence
      const scoreDiff = b.score - a.score;
      if (Math.abs(scoreDiff) > 100) {
        return scoreDiff;
      }

      // Si les scores sont proches (écart <= 100), privilégier le prix
      if (a.unitPrice === Infinity && b.unitPrice === Infinity) {
        return b.score - a.score;
      }
      if (a.unitPrice === Infinity) return 1;
      if (b.unitPrice === Infinity) return -1;

      // Sinon, trier principalement par prix (moins cher en premier)
      const priceDiff = a.unitPrice - b.unitPrice;

      // Si les prix sont très proches (< 5% de différence), départager par score
      const priceThreshold = Math.min(a.unitPrice, b.unitPrice) * 0.05;
      if (Math.abs(priceDiff) < priceThreshold) {
        return b.score - a.score;
      }

      return priceDiff;
    })
    .map(item => item.product);
}

/**
 * Trouve le produit le moins cher pour chaque sous-catégorie
 */
export function findCheapestBySubCategory(products) {
  if (products.length === 0) return new Map();

  const cheapestMap = new Map();

  // Grouper les produits par sous-catégorie
  const productsBySubCat = new Map();
  products.forEach(product => {
    if (!productsBySubCat.has(product.subCategory)) {
      productsBySubCat.set(product.subCategory, []);
    }
    productsBySubCat.get(product.subCategory).push(product);
  });

  // Pour chaque sous-catégorie, trouver le produit le moins cher
  productsBySubCat.forEach((products, subCategory) => {
    let cheapest = null;
    let cheapestPrice = Infinity;

    products.forEach(product => {
      const unitPrice = calculateUnitPrice(product);
      if (unitPrice < cheapestPrice) {
        cheapestPrice = unitPrice;
        cheapest = product;
      }
    });

    if (cheapest) {
      cheapestMap.set(subCategory, cheapest);
    }
  });

  return cheapestMap;
}

